import { imax, imin, mulBp, withinRange, iatan2 } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, CorniceRules, CorniceState, Enemy, SendaNpc } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The goat path (docs/sendas/artifice.md, chapter 1, act 1, «El sendero de las cabras»; the ground in
 * battle/taller-layout.ts). A ledge two hexes wide up the Flank Cliff, the sea far below. Grillo, Nadd's first golem,
 * goes ahead, slow, while the hero keeps within `wait` of him; he stops short of a stone's shadow. Stones come off the
 * nests above, their shadow and their dust on the ledge `warn` before (3 s), on the hero or on the way ahead of Grillo.
 * Goblin slingers climb into the nests ahead of him and throw at him (Grillo squeaks and points his lamp at each: they
 * can be climbed to, a step up off the ledge); soot crows come in flocks off the sea. Grillo doesn't fight: down, he
 * crawls on to the next rest and gets up there `rise` later, and the hero has to wait for him; down twice, it is lost.
 * At the door he knocks three times: won.
 *
 * Cost: one person, a handful of stones and slingers, looked at once a tick; the crows hunt by the common rule.
 */

const fresh = (r: CorniceRules): CorniceState => ({ way: 1, stones: [], next: r.stone.first, sling: r.sling.first, crow: r.crows.first, downs: 0, crawl: -1, rise: -1, slingers: [], knock: -1 });

const grilloOf = (s: BattleState): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === 'grillo');

/** A stone's shadow by a point (Grillo waits short of it). */
const shadowBy = (r: CorniceRules, w: CorniceState, x: number, y: number): boolean => {
  for (const st of w.stones) if (withinRange(st.x, st.y, x, y, r.stone.r + 350)) return true;
  return false;
};

/** Ticks to the next stone's landing of those coming (−1: none). */
export const corniceNext = (s: BattleState): number => {
  const w = s.senda?.cornice;
  if (!w || !w.stones.length) return -1;
  let t = 0x7fffffff;
  for (const st of w.stones) if (st.t < t) t = st.t;
  return imax(0, t - s.tick);
};

/** The next rest at or past a row of the way (the last: by the door). */
const restFrom = (r: CorniceRules, row: number): number => {
  for (const i of r.rests) if (i >= row) return i;
  return r.rests[r.rests.length - 1] as number;
};

const stones = (s: BattleState, r: CorniceRules, w: CorniceState, g: SendaNpc | undefined): void => {
  const S = r.stone;
  if (s.waveT >= w.next && w.knock < 0) {
    const h = s.heroes.find((x) => x.downTicks === 0);
    const ahead = r.way[imin(r.way.length - 1, w.way + rngRange(s.rng, 0, 3))] as { x: number; y: number };
    const on = h && rngInt(s.rng, 10000) < S.onHeroBp ? { x: h.x, y: h.y } : ahead;
    const x = on.x + rngRange(s.rng, -350, 350);
    const y = on.y + rngRange(s.rng, -300, 300);
    w.stones.push({ x, y, t: s.tick + S.warn });
    w.next = s.waveT + rngRange(s.rng, S.gap[0], S.gap[1]);
    ev(s, { t: 'senda', kind: 'stoneWarn', npc: '', x, y, n: S.warn });
  }
  if (!w.stones.length) return;
  let k = 0;
  for (const st of w.stones) {
    if (st.t > s.tick) {
      w.stones[k++] = st;
      continue;
    }
    ev(s, { t: 'senda', kind: 'stone', npc: '', x: st.x, y: st.y });
    for (const h of s.heroes) if (h.downTicks === 0 && withinRange(h.x, h.y, st.x, st.y, S.r)) damageHero(s, h, mulBp(h.stats.maxHp, S.heroBp), null, 'physical', true);
    if (g && g.hp > 0 && withinRange(g.x, g.y, st.x, st.y, S.r)) {
      g.hp = imax(0, g.hp - S.blows);
      ev(s, { t: 'senda', kind: g.hp > 0 ? 'hit' : 'down', npc: g.id, x: g.x, y: g.y });
    }
  }
  w.stones.length = k;
};

/** A slinger into a nest ahead of Grillo (one not taken), every so often; each throws at Grillo, or at the hero. */
const slingers = (s: BattleState, r: CorniceRules, w: CorniceState, g: SendaNpc | undefined, cue: (c: string) => void): void => {
  const L = r.sling;
  w.slingers = w.slingers.filter((x) => s.enemies.some((e) => e.id === x.id && e.hp > 0));
  if (s.waveT >= w.sling && w.knock < 0) {
    w.sling = s.waveT + L.every;
    let pick = -1;
    for (let i = 0; i < r.nests.length; i++) {
      const n = r.nests[i] as CorniceRules['nests'][number];
      if (n.row <= w.way || n.row > w.way + L.ahead + 3 || w.slingers.some((x) => x.nest === i)) continue;
      if (pick < 0 || Math.abs(n.row - w.way - L.ahead) < Math.abs((r.nests[pick] as CorniceRules['nests'][number]).row - w.way - L.ahead)) pick = i;
    }
    if (pick >= 0) {
      const n = r.nests[pick] as CorniceRules['nests'][number];
      const e = spawnEnemy(s, 'slinger', 1, false, 0, n.x, n.y);
      if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
      w.slingers.push({ id: e.id, nest: pick, cd: s.tick + L.reload });
      if (g) ev(s, { t: 'senda', kind: 'point', npc: g.id, x: n.x, y: n.y, n: pick });
      cue('sling');
    }
  }
  for (const x of w.slingers) {
    if (s.tick < x.cd) continue;
    const e = s.enemies.find((o) => o.id === x.id);
    if (!e || e.stun > 0) continue;
    x.cd = s.tick + L.reload;
    const h = s.heroes.find((o) => o.downTicks === 0 && withinRange(o.x, o.y, e.x, e.y, L.range));
    // At Grillo first (the one going by), else at the hero.
    if (g && g.hp > 0 && w.crawl < 0 && withinRange(g.x, g.y, e.x, e.y, L.range)) {
      e.facing = iatan2(g.y - e.y, g.x - e.x);
      g.hp = imax(0, g.hp - 1);
      ev(s, { t: 'senda', kind: 'sling', npc: g.id, x: g.x, y: g.y, n: x.nest });
      ev(s, { t: 'senda', kind: g.hp > 0 ? 'hit' : 'down', npc: g.id, x: g.x, y: g.y });
    } else if (h) {
      e.facing = iatan2(h.y - e.y, h.x - e.x);
      damageHero(s, h, mulBp(h.stats.maxHp, L.heroBp), e, 'physical', false);
      ev(s, { t: 'senda', kind: 'sling', npc: '', x: h.x, y: h.y, n: x.nest });
    } else continue;
    e.anim = 'attack';
    e.animT = 0;
  }
};

/** A flock of soot crows off the sea (the east), by Grillo, every so often. */
const crows = (s: BattleState, r: CorniceRules, w: CorniceState, g: SendaNpc | undefined): void => {
  if (s.waveT < w.crow || w.knock >= 0) return;
  w.crow = s.waveT + r.crows.every;
  const at = g ?? s.heroes[0];
  if (!at) return;
  for (let k = 0; k < r.crows.n; k++) {
    const e = spawnEnemy(s, 'crow', 1, false, 0, at.x + 6500 + k * 400, at.y - 1500 + k * 700);
    if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  }
};

export const corniceTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.cornice;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.cornice ??= fresh(r));
  if (!s.started) return;
  const g = grilloOf(s);
  const h = s.heroes.find((x) => x.downTicks === 0);
  stones(s, r, w, g);
  slingers(s, r, w, g, cue);
  crows(s, r, w, g);
  if (!g) return;
  g.moving = false;
  // Down: he crawls on to the next rest, lies there a while and gets up.
  if (g.hp <= 0 && w.crawl < 0 && w.knock < 0) {
    w.downs++;
    w.crawl = restFrom(r, w.way);
    ev(s, { t: 'senda', kind: 'crawl', npc: g.id, x: g.x, y: g.y, n: w.downs });
    cue(`down${w.downs}`);
    if (w.downs >= r.downs) return;
  }
  if (w.crawl >= 0) {
    if (w.downs >= r.downs) return;
    const p = r.way[w.crawl] as { x: number; y: number };
    if (!withinRange(g.x, g.y, p.x, p.y, 60)) {
      g.moving = true;
      stepToward(g, p.x, p.y, r.crawl);
      return;
    }
    if (w.rise < 0) w.rise = s.tick + r.rise;
    if (s.tick < w.rise) return;
    g.hp = g.maxHp;
    w.way = imin(r.way.length - 1, w.crawl + 1);
    w.crawl = -1;
    w.rise = -1;
    ev(s, { t: 'senda', kind: 'up', npc: g.id, x: g.x, y: g.y });
    cue('up');
    return;
  }
  if (w.knock >= 0) return;
  // At the door: three knocks.
  if (withinRange(g.x, g.y, r.door.x, r.door.y, r.reach)) {
    w.knock = s.tick;
    g.facing = 768;
    ev(s, { t: 'senda', kind: 'knock', npc: g.id, x: r.door.x, y: r.door.y });
    cue('door');
    return;
  }
  const p = w.way < r.way.length ? (r.way[w.way] as { x: number; y: number }) : r.door;
  // A shadow on him: back down a row, out of it (he knows the ledge).
  if (shadowBy(r, w, g.x, g.y)) {
    const back = r.way[imax(0, w.way - 2)] as { x: number; y: number };
    if (!shadowBy(r, w, back.x, back.y)) {
      g.moving = true;
      if (stepToward(g, back.x, back.y, r.pace)) w.way = imax(1, w.way - 1);
    }
    return;
  }
  // On while the hero keeps up and no shadow lies ahead.
  if (!h || !withinRange(h.x, h.y, g.x, g.y, r.wait) || shadowBy(r, w, p.x, p.y) || shadowBy(r, w, g.x, g.y)) return;
  g.moving = true;
  if (stepToward(g, p.x, p.y, r.pace)) {
    w.way++;
    if (w.way * 2 >= r.way.length) cue('half');
  }
};

/** The slingers keep to their nests (true: the act moves them). */
export const corniceHold = (s: BattleState, e: Enemy): boolean => {
  const w = s.senda?.cornice;
  if (!w || e.kind !== 'slinger') return false;
  if (!w.slingers.some((x) => x.id === e.id)) return false;
  if (e.anim !== 'attack' || e.animT > 6) e.anim = 'idle';
  return true;
};

/** Grillo's way up, row by row. */
export const corniceGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.cornice;
  const w = s.senda?.cornice;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: imin(w.way, r.way.length), need: r.way.length };
};

/** Won with Grillo's three knocks at the door; lost with him down the second time. */
export const corniceResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.cornice;
  const w = s.senda?.cornice;
  if (!r || !w) return null;
  if (w.downs >= r.downs) return 'lost';
  if (w.knock >= 0 && s.tick >= w.knock + 24) return 'won';
  return null;
};
